import { useState, useEffect } from 'react';
import { api } from '@/App';
import Layout from '@/components/Layout';
import ShareButtons from '@/components/ShareButtons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarIcon, Plus, MapPin, Clock, Users, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';

const Events = ({ user, onLogout }) => {
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: ''
  });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await api.post('/events', newEvent);
      toast.success('Event created successfully!');
      setNewEvent({
        title: '',
        description: '',
        date: '',
        time: '',
        location: ''
      });
      setDialogOpen(false);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleRSVP = async (eventId) => {
    try {
      const response = await api.post(`/events/${eventId}/rsvp`);
      if (response.data.rsvped) {
        toast.success('RSVP confirmed!');
      } else {
        toast.info('RSVP cancelled');
      }
      fetchEvents();
    } catch (error) {
      toast.error('Failed to update RSVP');
    }
  };

  const handleDeleteEvent = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await api.delete(`/events/${id}`);
        toast.success('Event deleted');
        fetchEvents();
      } catch (error) {
        toast.error('Failed to delete event');
      }
    }
  };

  const isRSVPed = (event) => {
    return event.rsvp_users && event.rsvp_users.includes(user.id);
  };

  const isPastEvent = (eventDate) => {
    return new Date(eventDate) < new Date();
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="events-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Events</h1>
            <p className="text-gray-600">Discover and create community events</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-lime-600 hover:bg-lime-700 text-white" data-testid="create-event-button">
                <Plus className="h-4 w-4 mr-2" />
                Create Event
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Create New Event</DialogTitle>
                <DialogDescription>Share an event with the community</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateEvent} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="event-title">Event Title</Label>
                  <Input
                    id="event-title"
                    placeholder="Event name"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    required
                    data-testid="event-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event-date">Date</Label>
                    <Input
                      id="event-date"
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                      required
                      data-testid="event-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event-time">Time</Label>
                    <Input
                      id="event-time"
                      type="time"
                      value={newEvent.time}
                      onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                      required
                      data-testid="event-time-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event-location">Location</Label>
                  <Input
                    id="event-location"
                    placeholder="Event location or virtual link"
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                    required
                    data-testid="event-location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event-description">Description</Label>
                  <Textarea
                    id="event-description"
                    placeholder="Describe the event..."
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    rows={4}
                    required
                    data-testid="event-description-input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-lime-600 hover:bg-lime-700 text-white" data-testid="event-submit-button">
                    Create Event
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading events...</p>
          </div>
        ) : events.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => {
              const rsvped = isRSVPed(event);
              const past = isPastEvent(event.date);
              return (
                <Card key={event.id} className={`hover:shadow-lg transition-all duration-300 flex flex-col ${
                  past ? 'opacity-60' : ''
                }`} data-testid={`event-card-${event.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {rsvped && (
                          <Badge className="bg-green-100 text-green-700">
                            <Check className="h-3 w-3 mr-1" />
                            RSVPed
                          </Badge>
                        )}
                        {past && <Badge variant="secondary">Past</Badge>}
                      </div>
                      {event.user_id === user.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteEvent(event.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-event-${event.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <CardTitle className="text-xl">{event.title}</CardTitle>
                    <CardDescription className="text-sm">
                      Hosted by <span className="font-medium">{event.org_name}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <p className="text-gray-700 text-sm mb-4">{event.description}</p>
                    <div className="space-y-2 mt-auto">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CalendarIcon className="h-4 w-4" />
                        <span>{new Date(event.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="h-4 w-4" />
                        <span>{event.time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="h-4 w-4" />
                        <span className="truncate">{event.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users className="h-4 w-4" />
                        <span>{event.rsvp_count || 0} attending</span>
                      </div>
                      
                      {/* Share Buttons */}
                      <div className="pt-3 border-t">
                        <p className="text-xs font-medium text-gray-700 mb-2">Share this event:</p>
                        <ShareButtons
                          title={`${event.title} - CEC Connect`}
                          description={`${event.description.substring(0, 100)}... on ${new Date(event.date).toLocaleDateString()} at ${event.time}`}
                          url={window.location.href}
                        />
                      </div>

                      <Button
                        onClick={() => handleRSVP(event.id)}
                        className={`w-full mt-4 ${
                          rsvped
                            ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                            : 'bg-lime-600 hover:bg-lime-700 text-white'
                        }`}
                        disabled={past}
                        data-testid={`rsvp-event-${event.id}`}
                      >
                        {past ? 'Event Ended' : rsvped ? 'Cancel RSVP' : 'RSVP'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No events scheduled yet.</p>
              <p className="text-gray-400 text-sm mt-2">Be the first to create an event!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Events;